import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  PrintOptionCollapsibleDto,
  PrintOptionCollapsibleFilterDto,
  PrintOptionCollapsibleFilterOptionDto,
} from '@/api-sdk';
import { FocusManagementMixin } from '@/components/configurator/focus-management-step-mixin';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';

import '@/components/configurator/step-collapsible/fg-collapsible-filter-item';
import '@/components/configurator/step-collapsible/print-option/fg-collapsible-print-option-personalisation';

@customElement('fg-collapsible-print-option')
@localized()
export class FgCollapsiblePrintOption extends FocusManagementMixin(LitElement) {
  @property({ type: Number })
  index!: number;

  @property({ type: Number })
  totalOptions = 0;

  @property({ type: Array })
  filters!: PrintOptionCollapsibleFilterDto[];

  @property({ type: Boolean })
  open?: boolean;

  @property({ type: Boolean })
  valid?: boolean;

  @property({ type: String })
  unprintedQueryString: string | null | undefined;

  @property({ type: Object })
  printOption?: PrintOptionCollapsibleDto;

  constructor() {
    super();
    this.setupFocusManagement('printOption');
  }

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }

    const lastSubStepIndex = this.filters.length;
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
            subStepIndex: lastSubStepIndex - 1,
            filterOptionLayout:
              this.filters[lastSubStepIndex - 1].filterOptionLayout,
          },
        },
      }),
    );
  }

  isOptionFocussed(
    filter: PrintOptionCollapsibleFilterDto,
    subStepIndex: number,
    filterOptionIndex: number,
  ): boolean {
    if (subStepIndex === 0 && filterOptionIndex === 0) {
      return this.focusFirstOption;
    }

    return filter.focus;
  }

  render() {
    if (!Array.isArray(this.filters) || this.filters.length === 0)
      return nothing;

    return html`
      ${this.renderNextStepButton(this.printOption?.title)}
      <div ?hidden=${!this.open}>
        ${this.filters.map(
          (filter: PrintOptionCollapsibleFilterDto, subStepIndex: number) => {
            const filterSubStepId = `filter-substep-${subStepIndex}`;
            return html`
              <div
                class="wrapper
                ${classMap({
                  selected: Boolean(filter.focus),
                })}"
              >
                <h3 class="filter-question" id=${filterSubStepId}>
                  ${filter.filterQuestion}
                </h3>
                <ul
                  aria-labelledby=${filterSubStepId}
                  class="filter-options
                ${classMap({
                    'has-most-chosen': filter.filterOptions?.some(
                      opt => opt.mostChosen,
                    ),
                    [`${filter.filterOptionLayout}`]:
                      filter.filterOptionLayout != null,
                  })}"
                >
                  ${filter.filterOptions?.map(
                    (
                      option: PrintOptionCollapsibleFilterOptionDto,
                      filterOptionIndex: number,
                    ) => html`
                      <fg-collapsible-filter-item
                        .option=${option}
                        .filterOptionLayout=${filter.filterOptionLayout}
                        .index=${this.index}
                        .subStepIndex=${subStepIndex}
                        .filterOptionIndex=${filterOptionIndex}
                        .filterIndex=${this.filters.length}
                        .focussed=${this.isOptionFocussed(
                          filter,
                          subStepIndex,
                          filterOptionIndex,
                        )}
                      ></fg-collapsible-filter-item>
                    `,
                  )}
                </ul>
              </div>
            `;
          },
        )}
        ${this.unprintedQueryString !== null &&
        this.unprintedQueryString !== undefined &&
        this.index === 0
          ? html`
              <div class="unprinted-button-container">
                <button
                  @click=${() =>
                    this.updateComponent(this.unprintedQueryString)}
                >
                  ${t('actions.iDontWantAnyPrinting')}
                </button>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .wrapper {
        border: none;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        margin-top: 16px;
        padding: 16px;
      }
      .wrapper.selected {
        background-color: var(
          --fg-tertiary--light,
          var(--fg-fallback-tertiary--light)
        );
        border: 1px solid var(--fg-info, var(--fg-fallback-info));
      }
      .filter-options.image {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }
      .filter-options.default {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(1, minmax(0, 1fr));
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
      .filter-options {
        clear: left;
        flex-wrap: wrap;
        display: flex;
        gap: 8px;
      }
      .filter-options.has-most-chosen {
        padding-top: 12px;
      }
      .filter-question {
        font-size: 1rem;
        float: left;
        clear: both;
        font-weight: 600;
        margin-bottom: 8px;
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
      }
      .unprinted-button-container {
        margin: 16px 0 0 16px;
      }

      .unprinted-button-container button {
        color: var(--fg-primary, var(--fg-fallback-primary));
        font-size: 1rem;
        text-decoration: underline;
      }
      .unprinted-button-container button:hover {
        text-decoration: none;
      }
    `,
  ];
}
